/**
 * BudgetStatusBadge Component
 * Displays budget lifecycle status with color coding and icon.
 *
 * Status IDs (from BudgetStatus DB table):
 *  0 = Draft, 1 = Submitted, 2 = Approved, 3 = Closed,
 * -2 = Rejected, -3 = Cancelled
 */

import { Badge, type ThemedBadgeProps } from '@/components/ui';
import {
  IconPencil,
  IconHourglass,
  IconCircleCheck,
  IconArchive,
  IconX,
  IconBan,
} from '@tabler/icons-react';

interface StatusInput {
  statusID: number;
  name?: string | null;
}

interface BudgetStatusBadgeProps extends Omit<ThemedBadgeProps, 'children'> {
  status: StatusInput | number | undefined | null;
  showIcon?: boolean;
}

const STATUS_CONFIG: Record<
  number,
  { label: string; color: string; icon: React.ReactNode }
> = {
  '-3': {
    label: 'Cancelled',
    color: 'gray',
    icon: <IconBan size={12} />,
  },
  '-2': {
    label: 'Rejected',
    color: 'red',
    icon: <IconX size={12} />,
  },
  '0': {
    label: 'Draft',
    color: 'gray',
    icon: <IconPencil size={12} />,
  },
  '1': {
    label: 'Submitted',
    color: 'yellow',
    icon: <IconHourglass size={12} />,
  },
  '2': {
    label: 'Approved',
    color: 'green',
    icon: <IconCircleCheck size={12} />,
  },
  '3': {
    label: 'Closed',
    color: 'gray',
    icon: <IconArchive size={12} />,
  },
};

export function BudgetStatusBadge({
  status,
  showIcon = true,
  variant = 'light',
  size = 'sm',
  ...props
}: BudgetStatusBadgeProps) {
  if (status === undefined || status === null) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  const statusID = typeof status === 'number' ? status : status.statusID;
  const statusName = typeof status === 'object' ? status.name : null;
  const config = STATUS_CONFIG[statusID];

  if (!config && !statusName) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  const label = statusName || config.label;
  const color = config?.color || 'gray';
  const icon = config?.icon;

  return (
    <Badge
      variant={variant}
      size={size}
      color={color}
      leftSection={showIcon && icon ? icon : undefined}
      {...props}
    >
      {label}
    </Badge>
  );
}
